import React from 'react';
import Header from '../../components/ui/Header';
import SEO from '../../components/SEO';
import ContactForm from './components/ContactForm';
import ContactInfo from './components/ContactInfo';
import LocationMap from './components/LocationMap';
import LiveChat from './components/LiveChat';
import { generatePageSEO } from '../../utils/seoData';
import Icon from '../../components/AppIcon';

const ContactAndLocation = () => {
  const breadcrumbItems = [
    { label: 'Início', path: '/homepage' },
    { label: 'Contato e Localização', path: '/contact-and-location' }
  ];

  const seoData = generatePageSEO('contact');

  const quickActions = [
    {
      title: 'Ligar Agora',
      description: 'Fale diretamente conosco',
      icon: 'Phone',
      action: () => window.open('tel:+5561996919001', '_blank'),
      color: 'bg-success'
    },
    {
      title: 'WhatsApp',
      description: 'Atendimento rápido',
      icon: 'MessageCircle',
      action: () => window.open('https://wa.me/5561998765432', '_blank'),
      color: 'bg-accent'
    },
    {
      title: 'E-mail',
      description: 'Envie sua mensagem',
      icon: 'Mail',
      action: () => window.open('mailto:estacaocrdf@gmail.com', '_blank'),
      color: 'bg-primary'
    },
    {
      title: 'Como Chegar',
      description: 'Abrir no Google Maps',
      icon: 'Navigation',
      action: () => window.open('https://maps.google.com?q=-15.7801234,-47.8901234', '_blank'),
      color: 'bg-secondary'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO {...seoData} />
      <Header />
      <main className="pt-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary/5 to-accent/5 py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            {/* Breadcrumb */}
            <nav className="flex items-center space-x-2 text-sm text-muted-foreground mb-8">
              {breadcrumbItems?.map((item, index) => (
                <React.Fragment key={index}>
                  {index > 0 && <Icon name="ChevronRight" size={14} />}
                  <a
                    href={item?.path}
                    className={`hover:text-foreground transition-colors duration-200 ${
                      index === breadcrumbItems?.length - 1 ? 'text-foreground font-medium' : ''
                    }`}
                  >
                    {item?.label}
                  </a>
                </React.Fragment>
              ))}
            </nav>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div className="space-y-6">
                <h1 className="text-4xl lg:text-5xl xl:text-6xl font-heading font-bold text-foreground leading-tight">
                  Contato e Localização
                </h1>
                <p className="text-xl lg:text-2xl text-muted-foreground leading-relaxed max-w-2xl">
                  Estamos aqui para ajudar! Entre em contato conosco ou visite-nos no Altiplano Leste - Paranoá. 
                  Nossa equipe está pronta para atender empresários, visitantes e parceiros comerciais.
                </p>
                
                <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-8 text-base text-muted-foreground">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                      <Icon name="MapPin" size={18} className="text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Altiplano Leste - Paranoá, Brasília</p>
                      <p className="text-sm text-muted-foreground">Localização privilegiada</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-10 h-10 bg-secondary/10 rounded-lg">
                      <Icon name="Clock" size={18} className="text-secondary" />
                    </div>
                    <div>
                  <p className="font-medium text-foreground">Diariamente: 6h-23h</p>
                      <p className="text-sm text-muted-foreground">Atendimento estendido</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                {quickActions?.map((action, index) => (
                  <div
                    key={index}
                    onClick={action?.action}
                    className="p-6 bg-card rounded-xl shadow-warm hover:shadow-warm-lg cursor-pointer transition-all duration-300 group border border-border/50 hover:border-primary/30"
                  >
                    <div className={`w-12 h-12 ${action?.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-200`}>
                      <Icon name={action?.icon} size={24} className="text-white" />
                    </div>
                    <h3 className="font-semibold text-foreground text-base mb-2 group-hover:text-primary transition-colors duration-200">
                      {action?.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {action?.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              {/* Contact Form */}
              <div className="lg:col-span-2">
                <ContactForm />
              </div>

              {/* Contact Information */}
              <div>
                <ContactInfo />
              </div>
            </div>
          </div>
        </section>

        {/* Location and Map */}
        <section className="py-16 lg:py-20 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <LocationMap />
          </div>
        </section>

        {/* Business Hours & Additional Info */}
        <section className="py-16 lg:py-20 bg-gradient-to-br from-accent/5 to-primary/5">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              {/* Business Hours */}
              <div className="bg-card rounded-xl shadow-warm p-8 border border-border/50">
                <div className="flex items-center space-x-4 mb-8">
                  <div className="flex items-center justify-center w-12 h-12 bg-accent rounded-xl">
                    <Icon name="Clock" size={24} className="text-accent-foreground" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-heading font-semibold text-foreground">
                      Horários de Funcionamento
                    </h3>
                    <p className="text-base text-muted-foreground">
                      Confira nossos horários de atendimento
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  {['Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado', 'Domingo'].map((day, index) => (
                    <div key={index} className="flex items-center justify-between p-4 rounded-xl bg-muted/50 hover:bg-muted/70 transition-colors duration-200">
                      <span className="font-medium text-foreground text-base">{day}</span>
                      <span className="text-foreground font-semibold text-base">6h às 23h</span>
                    </div>
                  ))}
                </div>

                <div className="mt-8 p-6 bg-primary/10 rounded-xl border border-primary/20">
                  <div className="flex items-start space-x-3">
                    <Icon name="Info" size={20} className="text-primary mt-1" />
                    <div>
                      <p className="text-base text-primary font-semibold mb-2">
                        Horários Especiais
                      </p>
                      <p className="text-sm text-primary/80 leading-relaxed">
                        Feriados e datas especiais podem ter horários diferenciados. 
                        Consulte nossa equipe para confirmação.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Services */}
              <div className="bg-card rounded-xl shadow-warm p-8 border border-border/50">
                <div className="flex items-center space-x-4 mb-8">
                  <div className="flex items-center justify-center w-12 h-12 bg-secondary rounded-xl">
                    <Icon name="Headphones" size={24} className="text-secondary-foreground" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-heading font-semibold text-foreground">
                      Serviços de Atendimento
                    </h3>
                    <p className="text-base text-muted-foreground">
                      Como podemos ajudar você
                    </p>
                  </div>
                </div>

                <div className="space-y-6">
                  {[
                    { icon: 'Building', title: 'Informações sobre Locação', description: 'Consultas sobre espaços disponíveis e condições comerciais', color: 'text-primary' },
                    { icon: 'Users', title: 'Parcerias Comerciais', description: 'Oportunidades de colaboração e networking empresarial', color: 'text-secondary' },
                    { icon: 'Calendar', title: 'Organização de Eventos', description: 'Suporte para eventos corporativos e atividades especiais', color: 'text-accent' },
                    { icon: 'HelpCircle', title: 'Suporte Geral', description: 'Informações sobre lojas, serviços e facilidades do edifício', color: 'text-muted-foreground' }
                  ].map((service, index) => (
                    <div key={index} className="flex items-start space-x-4 p-4 rounded-xl hover:bg-muted/30 transition-colors duration-200">
                      <div className={`flex items-center justify-center w-10 h-10 rounded-lg bg-muted/50 ${service.color.replace('text-', 'bg-').replace('foreground', 'foreground/20')}`}>
                        <Icon name={service.icon} size={20} className={service.color} />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-foreground text-base mb-2">
                          {service.title}
                        </h4>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {service.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      {/* Footer */}
      <footer className="bg-primary text-primary-foreground py-12">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 text-center">
          <div className="flex items-center justify-center space-x-4 mb-6">
            <div className="flex items-center justify-center w-12 h-12 bg-primary-foreground rounded-xl">
              <svg 
                viewBox="0 0 24 24" 
                className="w-6 h-6 text-primary"
                fill="currentColor"
              >
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
              </svg>
            </div>
            <span className="text-2xl font-heading font-bold">
              Estação Marina
            </span>
          </div>
          <p className="text-base text-primary-foreground/90">
            © {new Date()?.getFullYear()} Estação Marina da Rocha. Todos os direitos reservados.
          </p>
        </div>
      </footer>
      {/* Live Chat Component */}
      <LiveChat />
    </div>
  );
};

export default ContactAndLocation;